/**
 * Hook for D3 cloud layout
 * Wraps d3-cloud library with React lifecycle
 *
 * Supports WebWorker-based computation for better performance when available.
 * Falls back to main-thread computation on older browsers.
 */

import { useState, useRef, useCallback, useEffect } from 'react'

import CloudLayout from '@aytm/app/stats/page/question-views/components/word-cloud/hooks/CloudLayout'
import useCloudLayoutWorker, {
  createFontSizeScale,
  isWorkerSupported,
  LAYOUT_CANCELLED
} from '@aytm/app/stats/page/question-views/components/word-cloud/hooks/useCloudLayoutWorker'

/**
 * Hook to compute word cloud layout using d3-cloud
 *
 * @param {Object} options - Layout options
 * @param {Array} options.words - Processed words array
 * @param {number} options.width - Canvas width
 * @param {number} options.height - Canvas height
 * @param {string} options.fontFamily - Font family
 * @param {number[]} options.angles - Rotation angles
 * @param {string} options.scaleFunction - Font size scale type
 * @param {number} options.rangeStart - Min font size
 * @param {number} options.rangeEnd - Max font size
 * @param {number[]} options.mask32 - Mask bitmap (optional)
 * @param {number[]} options.maskBound - Mask bounds (optional)
 * @param {number} options.randIndex - Random seed index
 * @param {boolean} options.mergeWords - Whether merge mode is on
 * @param {string} options.activeWord - Currently active word
 * @param {boolean} options.useWorker - Force WebWorker usage (default: auto-detect)
 * @returns {{ layoutWords: Array, error: Error|null, isComputing: boolean }}
 */
export function useCloudLayout(options) {
  const {
    words,
    width,
    height,
    fontFamily,
    angles,
    scaleFunction,
    rangeStart,
    rangeEnd,
    mask32 = [],
    maskBound = [],
    randIndex = 0,
    mergeWords = true,
    boundedRuntime = false
  } = options

  const workerHook = useCloudLayoutWorker()

  const [layoutWords, setLayoutWords] = useState([])
  const [isComputing, setIsComputing] = useState(false)

  const [error, setError] = useState(null)

  const workerHookRef = useRef(null)
  workerHookRef.current = workerHook

  const layoutRef = useRef(null)
  const angleCounterRef = useRef(0)

  // Main-thread fallback computation
  const computeWithoutWorker = useCallback(() => {
    // Calculate min/max times for scaling
    let minTimes = Infinity
    let maxTimes = -Infinity

    const mergeField = mergeWords ? 'times' : 'f'

    for (const word of words) {
      minTimes = Math.min(minTimes, word[mergeField])
      maxTimes = Math.max(maxTimes, word[mergeField])
    }

    const fontSizeScale = createFontSizeScale(scaleFunction, [minTimes, maxTimes], [rangeStart, rangeEnd])

    // Stop any existing layout
    // if (layoutRef.current) {
    //   layoutRef.current.stop()
    // }

    try {
      const layout = new CloudLayout()
        .setSize([width - 5, height - 5])
        .setWords(words)
        .setSpiral('rectangular')
        .setPadding(1)
        .setRotate(() => {
          if (!angles.length) return 0
          angleCounterRef.current++
          return angles[angleCounterRef.current % angles.length]
        })
        .setFont(fontFamily)
        .setFontSize(d => fontSizeScale(d[mergeField]))
        .randIndex(randIndex)
        .setBringWordsCloser(25 <= words.length)
        .setBoundedRuntime(boundedRuntime)

      if (mask32 && mask32.length > 0 && maskBound && maskBound.length === 4) {
        layout.setMask(mask32, maskBound)
      }

      layoutRef.current = layout
      const result = layout.start()

      setLayoutWords(result.tags)
      setIsComputing(false)
    } catch (error) {
      setError(error)
      setIsComputing(false)
    }
  }, [
    words,
    width,
    height,
    fontFamily,
    angles,
    scaleFunction,
    rangeStart,
    rangeEnd,
    mask32,
    maskBound,
    randIndex,
    mergeWords,
    boundedRuntime
  ])

  // WebWorker-based computation
  const computeWithWorker = useCallback(async () => {
    const mergeField = mergeWords ? 'times' : 'f'

    // Calculate min/max times for scaling
    let minTimes = Infinity
    let maxTimes = -Infinity

    for (const word of words) {
      minTimes = Math.min(minTimes, word[mergeField])
      maxTimes = Math.max(maxTimes, word[mergeField])
    }

    const config = {
      words: words,
      size: [width - 5, height - 5],
      font: fontFamily,
      padding: 1,
      angles,
      scaleFunction,
      minTimes,
      maxTimes,
      rangeStart,
      rangeEnd,
      mergeField,
      randIndex,
      bringWordsCloser: 25 <= words.length,
      mask32: mask32,
      maskBound: maskBound,
      boundedRuntime
    }

    try {
      const result = await workerHookRef.current.computeLayout(config)
      setLayoutWords(result.tags)
      setIsComputing(false)
    } catch (error) {
      // A cancelled run was replaced by a newer one, or the block is gone. Retrying it on the main
      // thread would compute a stale layout and write it over the live one -- or over nothing.
      if (LAYOUT_CANCELLED === error?.code) return

      // On worker error, fall back to main thread
      computeWithoutWorker()
    }
  }, [
    words,
    width,
    height,
    fontFamily,
    angles,
    scaleFunction,
    rangeStart,
    rangeEnd,
    mask32,
    maskBound,
    randIndex,
    mergeWords,
    boundedRuntime,
    computeWithoutWorker
  ])

  const compute = useCallback(() => {
    // nothing to lay out is a finished layout, not a pending one -- leaving the flag raised here
    // strands every reader of it on a cloud that never arrives
    if (!words || words.length === 0 || width <= 0 || height <= 0) {
      setLayoutWords([])
      setIsComputing(false)
      return
    }

    setIsComputing(true)
    setError(null)

    angleCounterRef.current = 0

    if (isWorkerSupported()) {
      computeWithWorker()
    } else {
      computeWithoutWorker()
    }
  }, [width, height, words, computeWithWorker, computeWithoutWorker])

  // Compute layout when dependencies change
  useEffect(() => {
    compute()

    return () => {
      // if (layoutRef.current) {
      //   layoutRef.current.stop()
      // }
      workerHookRef.current.cancel()
    }
  }, [compute])

  return { layoutWords, error, isComputing }
}

export default useCloudLayout
